import { Fragment } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type TableMeta,
} from '@tanstack/react-table';

import { Card } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

/** Uma sub-linha da linha aberta: cada valor de `cells` cai na coluna do seu id. */
export interface DataTableSubRow {
  id: string;
  cells: Partial<Record<string, React.ReactNode>>;
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  /** Stable id per row — used for `activeRowId` and React keys. */
  getRowId: (row: T) => string;
  /** Click handler for the whole row; cells can opt out with onClick.stopPropagation(). */
  onRowClick?: (row: T) => void;
  /** Highlights the matching row in brand-tint. */
  activeRowId?: string | null;
  /** Empty-state copy when `data` is empty. */
  emptyMessage?: string;
  /** Open rows: each gets, right below it, a full-width row with `renderExpanded`. */
  expandedRowIds?: ReadonlySet<string>;
  renderExpanded?: (row: T) => React.ReactNode;
  /** Sub-linhas da linha aberta, com uma célula por id de coluna; `null` usa `renderExpanded`. */
  renderSubRows?: (row: T) => DataTableSubRow[] | null;
  /**
   * Valores que mudam a cada render, lidos pelas células em `table.options.meta`. Assim as colunas
   * podem ficar fora do componente: `flexRender` monta cada `cell` como componente, e uma função
   * nova a cada render remonta todas as células.
   */
  meta?: TableMeta<T>;
  /** Linhas e células mais baixas (`h-9`, `py-1.5`), para grids longas. */
  dense?: boolean;
}

/**
 * DataTable — thin wrapper over `@tanstack/react-table` that owns the
 * visual contract for tables in this app: bordered `Card` shell,
 * mono eyebrow headers, hover-on-row and active-row tint. Sorting /
 * filtering / pagination live on the table model — pass them via the
 * tanstack-table column defs and table options so the visual surface
 * stays untouched.
 */
export function DataTable<T>({
  columns,
  data,
  getRowId,
  onRowClick,
  activeRowId,
  emptyMessage,
  expandedRowIds,
  renderExpanded,
  renderSubRows,
  meta,
  dense = false,
}: Readonly<DataTableProps<T>>): React.JSX.Element {
  const rowHeight = dense ? 'h-9' : 'h-11';
  const cellPadding = dense ? 'py-1.5' : 'py-2.5';
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    meta,
  });

  return (
    <Card>
      <div className="overflow-hidden rounded-lg">
        <table className="w-full border-collapse text-meta">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    className="whitespace-nowrap border-b border-border bg-background px-3 py-2 text-left font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-faint-foreground"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-5 py-10 text-center text-muted-foreground"
                >
                  {emptyMessage ?? '—'}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const active = activeRowId === row.id;
                const open = expandedRowIds?.has(row.id) ?? false;
                const subRows = open ? (renderSubRows?.(row.original) ?? null) : null;
                const expanded = open && !subRows && renderExpanded;
                return (
                  <Fragment key={row.id}>
                    <tr
                      onClick={
                        onRowClick
                          ? () => {
                              onRowClick(row.original);
                            }
                          : undefined
                      }
                      className={twcn(
                        rowHeight,
                        'border-b border-border last:border-b-0',
                        onRowClick && 'cursor-pointer hover:bg-muted',
                        active && 'bg-primary-tint/60 hover:bg-primary-tint/60',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={twcn('px-3 align-middle text-foreground-default', cellPadding)}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {subRows?.map((sub) => (
                      <tr
                        key={sub.id}
                        className="border-b border-dashed border-border bg-background last:border-b-0"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={twcn(
                              'px-3 align-middle text-foreground-default',
                              cellPadding,
                            )}
                          >
                            {sub.cells[cell.column.id]}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {expanded ? (
                      <tr
                        className="border-b border-border bg-background last:border-b-0"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <td colSpan={columns.length} className="p-0">
                          {renderExpanded(row.original)}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
